import { createContext, type ReactNode, useContext, useMemo, useState } from 'react';
import type { FieldMetadata, FieldValue } from '../data/client';

/**
 * ONE context, PROP-LESS field components.
 *
 * This is the single best idea in Twenty's frontend: a field display or input
 * takes zero props and reads everything it needs from here. Adding "who edited
 * this" or "is this read-only" is then a change to one context, not to 24
 * component signatures - and a table cell, a record page section and the kitchen
 * sink all render the exact same component.
 */
export interface FieldContextValue {
  readonly objectId: string;
  readonly recordId: string;
  readonly field: FieldMetadata;
  readonly value: FieldValue;
  /**
   * Inputs call this; displays ignore it.
   *
   * IT MAY REPORT WHEN THE WRITE IT TRIGGERED HAS SETTLED (`file-attachments.md` D18; wave
   * 1c, finding 3). Every other input can ignore the result, and every one of them does: a
   * text field has nothing to do between the keystroke and the row coming back. FILES does,
   * because an attachment is not READABLE the moment its value is on screen - the bytes are
   * on the server but the record write is what LINKS them, and `/api/files/:id/content`
   * correctly answers 404 for a file that is not linked yet. A surface that renders an
   * `<img>` from the optimistic value therefore renders a broken one.
   *
   * The two surfaces that write a record return the mutation's promise; the fixture provider
   * and the kitchen sink return nothing, which reads as "already settled" and is true of
   * them.
   */
  readonly onChange: (next: FieldValue) => Promise<void> | void;
  readonly readOnly: boolean;
  /**
   * INV-5: a human corrected this field, so automated writers are refused.
   *
   * There is deliberately NO "refused by" here. A refusal names who HOLDS the
   * field (`WriteResult.write_blocked.blocked[].heldBy`), never the writer that
   * was turned away - the client does not know its own actor, the server derives
   * it from the session. Surfacing the refusal is therefore the job of the
   * surface that made the write, not of the field component.
   */
  readonly humanOwned: boolean;
  /**
   * THE LABEL A QUERY ALREADY RESOLVED (record-page-and-views.md D3).
   *
   * `POST /api/query` returns a relation as `{id, label}` and resolves the labels in
   * one statement per relation FIELD. Present here, `RelationDisplay` renders it and
   * makes no request at all; absent - a single-record read, which returns bare ids -
   * it falls back to `useRelationLabel` exactly as before.
   *
   * It is NOT part of `value` on purpose: `value` is what the shared JS evaluator
   * reads (INV-6), and a `{id, label}` object where a uuid is expected makes every
   * RELATION condition evaluate false.
   */
  readonly relationLabel?: string | undefined;
  /**
   * THE VALUE IS NOT THE LINK HERE - A SMALL ICON BESIDE IT IS (audit P1-8).
   *
   * An email, a phone number, a URL and a relation all render as links, and a link
   * swallows the click that lands on it. In a table cell that is nearly the whole cell:
   * the anchor in an Email column measured 257px of a 289px cell, so 89% of an EDITABLE
   * cell could not start an edit, and the edit target was a strip whose position depended
   * on how long the value happened to be.
   *
   * Set by the table cell and by nothing else. The record page, the phone cards, the
   * related rows and the dashboard panels are READ surfaces, where the value being the
   * link is exactly right: nothing there competes for the same pixels.
   */
  readonly linkAsIcon?: boolean | undefined;
  /**
   * THE VALUE IS BEING RENDERED IN ONE TABLE ROW (`file-attachments.md` D18).
   *
   * Twelve of the thirteen field types render the SAME in a cell and on a record page,
   * which is why this context has needed nothing like it until now. A FILES value does not:
   * the record page shows a vertical list of rows with a size, a preview and two actions per
   * file, and putting that inside a 54px table row would make one row as tall as a card.
   * The cell shows the first label plus a count instead (D18), and this is how it says so.
   *
   * Set by `displayContext` in the table and by nothing else, exactly like `linkAsIcon`
   * above. Reusing THAT flag was the alternative and was refused: it means "the value is not
   * the link here", which is a statement about anchors and would read as a lie on a
   * component that renders none.
   */
  readonly dense?: boolean | undefined;
  /**
   * THE REST OF THE ROW THIS VALUE SITS IN (`docs/decisions/phase-b-coach.md` D1, D5).
   *
   * Twelve of the thirteen field types are a function of their own value alone, which is why
   * this context has needed nothing like it until now. A CHECKPOINT date is not: its deadline
   * is `base + offsetDays`, where the base is ANOTHER field of the same record, so the cell
   * cannot tell "due" from "late" without it.
   *
   * Set by `displayContext` in the table and by nothing else, exactly like `dense` above. Its
   * ABSENCE is a real answer rather than a gap: D5 says a view that did not select the base
   * column draws a plain ring instead of guessing, and `checkpointState` produces exactly
   * that from a row the base is missing from.
   */
  readonly row?: Readonly<Record<string, FieldValue>> | undefined;
  /**
   * A SELECT VALUE IS TEXT WITH A COLOUR DOT, NOT A TINTED CAPSULE (owner, 2026-09-10;
   * P4 round 2 finding F1, P5 round 2 finding F1, unified in P5 round 3).
   *
   * The capsule is a WEB badge and Apple uses none for a stored value: Finder's tags -
   * the one place in the system where an option carries a colour - are a filled circle
   * beside plain label text (MEASURED
   * `support-get-to-know-the-finder-mchlp2605__S0051_FinderWindow.png`). The colour is a
   * handle for finding the value again; the label is the value, and a capsule makes it
   * look like a control.
   *
   * ONE FLAG FOR THE WHOLE PRODUCT, and that is the point of it. Two slices arrived at
   * this rule in the same week from two screens, and until they merged the record's read
   * surfaces and the list's rows drew the same option out of two components. Set by
   * `app/record/ReadOnlyFieldValue.tsx` (every read surface of a record's own values),
   * by `RecordTableCell` and by `RecordCard`; anything else - a board card, a dashboard
   * panel - still draws the capsule until its own slice decides.
   */
  readonly tagAsDot?: boolean | undefined;
  /**
   * THE VALUE IS ONE OF A LIST ROW'S OWN VALUES (P4 round 2, findings F1 and F5).
   *
   * A list row is the one surface where a value is read by SCANNING a column of them, and
   * nothing in Finder, Mail or Reminders paints a value in the link colour there - the ROW
   * is the link. So a value that is normally an anchor (a relation, an email, a URL, a
   * phone number) renders as PLAIN text when the row itself is the link. It does NOT apply
   * where `linkAsIcon` is set: a table cell keeps P1-8's small jump icon, because there the
   * cell is an editor rather than a link.
   *
   * THE DOT IS NOT THIS FLAG'S (P5 round 3). It was, while the list was the only surface
   * that had taken the rule; the record's values are read the same way and are still
   * anchors, because there the row is not a link. `tagAsDot` above carries the dot for
   * both, and a list row sets both flags.
   */
  readonly listRow?: boolean | undefined;
}

const FieldContext = createContext<FieldContextValue | null>(null);

export function FieldProvider({
  value,
  children,
}: {
  readonly value: FieldContextValue;
  readonly children: ReactNode;
}) {
  return <FieldContext.Provider value={value}>{children}</FieldContext.Provider>;
}

export function useFieldContext(): FieldContextValue {
  const context = useContext(FieldContext);
  if (context === null) {
    throw new Error('Field components must be rendered inside <FieldProvider>');
  }
  return context;
}

/**
 * A self-contained provider that holds the value in local state. It backs the
 * kitchen sink and the component tests, and it is what makes a field component
 * demonstrable without a record, a query client or an API.
 */
export function FixtureFieldProvider({
  field,
  initialValue,
  readOnly = false,
  humanOwned = false,
  onChange,
  children,
}: {
  readonly field: FieldMetadata;
  readonly initialValue: FieldValue;
  readonly readOnly?: boolean | undefined;
  readonly humanOwned?: boolean | undefined;
  readonly onChange?: ((next: FieldValue) => void) | undefined;
  readonly children: ReactNode;
}) {
  const [value, setValue] = useState<FieldValue>(initialValue);

  const context = useMemo<FieldContextValue>(
    () => ({
      objectId: field.objectId,
      recordId: `fixture-${field.id}`,
      field,
      value,
      readOnly,
      humanOwned,
      onChange: (next) => {
        setValue(next);
        onChange?.(next);
      },
    }),
    [field, value, readOnly, humanOwned, onChange],
  );

  return <FieldProvider value={context}>{children}</FieldProvider>;
}
